import * as React from "react";
import { EditableTable } from "../../../../../../components/Tables/NewEditableTable";
import { Box, Typography } from "@mui/material";
import { useMIS } from "../MISContext";

const fontSize = "14px";
const cellPadding = "12px";

const monthKeys = [
  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec",
];


export default function ReceivablesTab() {
  const { receivablesRows, setReceivablesRows, hoursRows: contextHoursRows, poPayablesData } = useMIS();

  // Helper to get month key from date
  const getMonthKey = (dateValue) => {
    if (!dateValue) return null;
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return null;
    return monthKeys[date.getMonth()] || null;
  };

  // Combined Hours table: Engineering + Fabrication from context hoursRows (actualHours)
  const hoursRows = React.useMemo(() => {
    const engineeringRow = (contextHoursRows || []).find(
      (row) => row.task?.toLowerCase() === "engineering"
    );
    const fabricationRow = (contextHoursRows || []).find(
      (row) => row.task?.toLowerCase() === "fabrication"
    );

    // Build Engineering row with all months
    const engData = { id: 1, discipline: "Engineering" };
    monthKeys.forEach((key) => {
      engData[key] = engineeringRow?.[key] || "";
    });
    const engTotal = monthKeys.reduce(
      (sum, key) => sum + (Number(engineeringRow?.[key]) || 0),
      0
    );

    // Build Fabrication row with all months
    const fabData = { id: 2, discipline: "Fabrication" };
    monthKeys.forEach((key) => {
      fabData[key] = fabricationRow?.[key] || "";
    });
    const fabTotal = monthKeys.reduce(
      (sum, key) => sum + (Number(fabricationRow?.[key]) || 0),
      0
    );

    // Build Total row - sum of Engineering and Fabrication for each month
    const totalData = { id: 3, discipline: "Total" };
    monthKeys.forEach((key) => {
      const engVal = Number(engineeringRow?.[key]) || 0;
      const fabVal = Number(fabricationRow?.[key]) || 0;
      totalData[key] = engVal + fabVal || "";
    });
    totalData.total = engTotal + fabTotal;

    return [engData, fabData, totalData];
  }, [contextHoursRows]);

  // Extract Material & Subcontractor cost from poPayablesData (payables)
  const costRows = React.useMemo(() => {
    // Group payables by month
    const monthlyTotals = {};
    monthKeys.forEach((key) => {
      monthlyTotals[key] = 0;
    });

    (poPayablesData || []).forEach((payment) => {
      const monthKey = getMonthKey(payment.paymentDate);
      if (monthKey && monthKeys.includes(monthKey)) {
        monthlyTotals[monthKey] += Number(payment.amount) || 0;
      }
    });

    // Build Procurement row with all months
    const dataRow = { id: 1, discipline: "Procurement" };
    monthKeys.forEach((key) => {
      dataRow[key] = monthlyTotals[key] || "";
    });

    const total = monthKeys.reduce(
      (sum, key) => sum + (monthlyTotals[key] || 0),
      0
    );

    // Build Total row
    const totalRow = { id: 2, discipline: "Total" };
    monthKeys.forEach((key) => {
      totalRow[key] = monthlyTotals[key] || "";
    });
    totalRow.total = total;

    return [dataRow, totalRow];
  }, [poPayablesData]);

  // Helper function to calculate cumulative progress for a row up to a specific month (capped at 100)
  const getCumulativeProgress = React.useCallback((row, monthIndex) => {
    const monthsUpToNow = monthKeys.slice(0, monthIndex + 1);
    const sum = monthsUpToNow.reduce((acc, m) => acc + (Number(row[m]) || 0), 0);
    return Math.min(sum, 100); // Cap at 100 since max progress per task is 100
  }, []);

  const columns = React.useMemo(() => {
    const monthLabel = (key) => key.charAt(0).toUpperCase() + key.slice(1);

    // Total possible progress = number of tasks * 100
    const maxProgress = receivablesRows.length * 100;

    // Calculate average cumulative footer values for each month
    // Footer = average of all tasks' average progress up to that month
    const cumulativeFooters = {};
    monthKeys.forEach((key, index) => {
      let totalAvgProgress = 0;
      let taskCount = 0;
      receivablesRows.forEach((row) => {
        // Get average progress for this row up to this month
        const monthsUpToNow = monthKeys.slice(0, index + 1);
        const values = monthsUpToNow
          .map((m) => Number(row[m]) || 0)
          .filter((v) => v > 0);
        if (values.length > 0) {
          const avgProgress = values.reduce((sum, v) => sum + v, 0) / values.length;
          totalAvgProgress += avgProgress;
          taskCount += 1;
        }
      });
      // Average of all tasks' average progress
      cumulativeFooters[key] = taskCount > 0 ? totalAvgProgress / taskCount : 0;
    });

    const monthColumns = monthKeys.map((key, index) => ({
      accessorKey: key,
      header: monthLabel(key),
      size: 50,
      // Use Cell to display average cumulative progress up to this month
      Cell: ({ row }) => {
        const monthsUpToNow = monthKeys.slice(0, index + 1);
        // Get all months that have values
        const values = monthsUpToNow
          .map((m) => Number(row.original[m]) || 0)
          .filter((v) => v > 0);
        if (!values.length) return "";
        // Average progress = sum / count of months with data
        const avgProgress = values.reduce((sum, v) => sum + v, 0) / values.length;
        return `${avgProgress.toFixed(2)}%`;
      },
      footer: cumulativeFooters[key] ? `${cumulativeFooters[key].toFixed(2)}%` : "0.00%",
      muiTableHeadCellProps: {
        sx: { fontSize, padding: cellPadding },
      },
      muiTableBodyCellProps: {
        sx: { fontSize, padding: cellPadding },
      },
      muiTableFooterCellProps: {
        sx: { fontSize, padding: cellPadding },
      },
    }));

    // Calculate total cumulative progress percentage
    const totalCumulative = cumulativeFooters["dec"] || 0;

    return [
      {
        accessorKey: "task",
        header: "Tasks",
        size: 180,
        footer: `Avg Progress`,
        muiTableHeadCellProps: {
          sx: { fontSize, padding: cellPadding },
        },
        muiTableBodyCellProps: {
          sx: { fontSize, padding: cellPadding },
        },
        muiTableFooterCellProps: {
          sx: { fontSize, padding: cellPadding },
        },
      },
      {
        header: "Progress %",
        muiTableHeadCellProps: {
          sx: { fontSize, padding: cellPadding },
        },
        columns: monthColumns,
      },
      {
        id: "total",
        header: "Cumulative Progress",
        size: 60,
        enableEditing: false,
        Cell: ({ row }) => {
          // Average of all monthly progress values for this task
          const values = monthKeys
            .map((key) => Number(row.original[key]) || 0)
            .filter((v) => v > 0);
          if (!values.length) return "";
          const avgProgress = values.reduce((sum, v) => sum + v, 0) / values.length;
          return `${avgProgress.toFixed(2)}%`;
        },
        footer: totalCumulative ? `${totalCumulative.toFixed(2)}%` : "0.00%",
        muiTableHeadCellProps: {
          sx: { fontSize, padding: cellPadding },
        },
        muiTableBodyCellProps: {
          sx: { fontSize, padding: cellPadding },
        },
        muiTableFooterCellProps: {
          sx: { fontSize, padding: cellPadding },
        },
      },
    ];
  }, [receivablesRows, getCumulativeProgress]);

  // Columns for Hours table (Engineering + Fabrication) - all 12 months
  const hoursColumns = React.useMemo(() => {
    const monthLabel = (key) => key.charAt(0).toUpperCase() + key.slice(1);

    const monthCols = monthKeys.map((key) => ({
      accessorKey: key,
      header: monthLabel(key),
      size: 50,
      muiTableHeadCellProps: {
        sx: { fontSize, padding: cellPadding },
      },
      muiTableBodyCellProps: {
        sx: { fontSize, padding: cellPadding },
      },
    }));

    return [
      {
        accessorKey: "discipline",
        header: "Discipline",
        size: 120,
        muiTableHeadCellProps: {
          sx: { fontSize, padding: cellPadding },
        },
        muiTableBodyCellProps: {
          sx: { fontSize, padding: cellPadding },
        },
      },
      ...monthCols,
      {
        id: "total",
        header: "Total",
        size: 70,
        accessorFn: (row) =>
          monthKeys.reduce((sum, key) => sum + (Number(row[key]) || 0), 0),
        muiTableHeadCellProps: {
          sx: { fontSize, padding: cellPadding },
        },
        muiTableBodyCellProps: {
          sx: { fontSize, padding: cellPadding },
        },
      },
    ];
  }, []);

  // Columns for Cost table (Material & Subcontractor) - all 12 months
  const costColumns = React.useMemo(() => {
    const monthLabel = (key) => key.charAt(0).toUpperCase() + key.slice(1);

    const monthCols = monthKeys.map((key) => ({
      accessorKey: key,
      header: monthLabel(key),
      size: 50,
      muiTableHeadCellProps: {
        sx: { fontSize, padding: cellPadding },
      },
      muiTableBodyCellProps: {
        sx: { fontSize, padding: cellPadding },
      },
    }));

    return [
      {
        accessorKey: "discipline",
        header: "Category",
        size: 120,
        muiTableHeadCellProps: {
          sx: { fontSize, padding: cellPadding },
        },
        muiTableBodyCellProps: {
          sx: { fontSize, padding: cellPadding },
        },
      },
      ...monthCols,
      {
        id: "total",
        header: "Total",
        size: 70,
        accessorFn: (row) =>
          monthKeys.reduce((sum, key) => sum + (Number(row[key]) || 0), 0),
        muiTableHeadCellProps: {
          sx: { fontSize, padding: cellPadding },
        },
        muiTableBodyCellProps: {
          sx: { fontSize, padding: cellPadding },
        },
      },
    ];
  }, []);


  const hooks = React.useMemo(
    () => ({
      useCreateItem: () => ({
        mutateAsync: async ({ values }) => {
          setReceivablesRows((prev) => [
            ...prev,
            { ...values, id: Date.now() },
          ]);
        },
        isPending: false,
      }),
      useGetItems: () => ({
        data: receivablesRows,
        isError: false,
        isFetching: false,
        isLoading: false,
      }),
      useUpdateItem: () => ({
        mutateAsync: async ({ values }) => {
          setReceivablesRows((prev) =>
            prev.map((row) => (row.id === values.id ? values : row))
          );
        },
        isPending: false,
      }),
      useDeleteItem: () => ({
        mutateAsync: async ({ id }) => {
          setReceivablesRows((prev) => prev.filter((row) => row.id !== id));
        },
        isPending: false,
      }),
    }),
    [receivablesRows, setReceivablesRows]
  );

  // Read-only hooks for Hours table (Engineering + Fabrication)
  const hoursHooks = React.useMemo(
    () => ({
      useCreateItem: () => ({
        mutateAsync: async () => {},
        isPending: false,
      }),
      useGetItems: () => ({
        data: hoursRows,
        isError: false,
        isFetching: false,
        isLoading: false,
      }),
      useUpdateItem: () => ({
        mutateAsync: async () => {},
        isPending: false,
      }),
      useDeleteItem: () => ({
        mutateAsync: async () => {},
        isPending: false,
      }),
    }),
    [hoursRows]
  );

  // Read-only hooks for Cost table (Material & Subcontractor)
  const costHooks = React.useMemo(
    () => ({
      useCreateItem: () => ({
        mutateAsync: async () => {},
        isPending: false,
      }),
      useGetItems: () => ({
        data: costRows,
        isError: false,
        isFetching: false,
        isLoading: false,
      }),
      useUpdateItem: () => ({
        mutateAsync: async () => {},
        isPending: false,
      }),
      useDeleteItem: () => ({
        mutateAsync: async () => {},
        isPending: false,
      }),
    }),
    [costRows]
  );

  return (
    <Box
      width="100%"
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      <Typography variant="h6" align="center" sx={{ fontWeight: 600, mb: 1 }}>
        Account receivables
      </Typography>

      {/* Top left large table: % Progress complete */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Box sx={{ width: { xs: "100%", md: "70%" } }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            % Progress complete
          </Typography>
          <EditableTable
            name="Receivables"
            columns={columns}
            hooks={hooks}
            editMode="cell"
            // enableCreate={true}
            enableEditing={true}
            enableDelete={true}
            initialData={{
              task: "",
              jan: "",
              feb: "",
              mar: "",
              apr: "",
              may: "",
              jun: "",
              jul: "",
              aug: "",
              sep: "",
              oct: "",
              nov: "",
              dec: "",
            }}
            data={receivablesRows}
          />
        </Box>
      </Box>

      {/* Bottom two tables: Hours (Engineering + Fabrication) and Cost */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        {/* Hours table - Engineering + Fabrication */}
        <Box sx={{ width: "100%" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Hours (Engineering &amp; Fabrication)
          </Typography>
          <EditableTable
            name="Hours"
            columns={hoursColumns}
            hooks={hoursHooks}
            editMode="cell"
            enableEditing={false}
            enableDelete={false}
            initialData={{
              discipline: "",
              ...monthKeys.reduce((acc, key) => ({ ...acc, [key]: "" }), {}),
            }}
            data={hoursRows}
          />
        </Box>

        {/* Cost table - Material & Subcontractor */}
        <Box sx={{ width: "100%" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Material &amp; Subcontractor Cost
          </Typography>
          <EditableTable
            name="Cost"
            columns={costColumns}
            hooks={costHooks}
            editMode="cell"
            enableEditing={false}
            enableDelete={false}
            initialData={{
              discipline: "",
              ...monthKeys.reduce((acc, key) => ({ ...acc, [key]: "" }), {}),
            }}
            data={costRows}
          />
        </Box>
      </Box>
    </Box>
  );
}

